import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Toaster, toast } from "sonner";
import { ArrowLeft, BookOpen, Search, Sparkles, MessageCircle, Send, Check, X as XIcon, Printer } from "lucide-react";
import { EDUCATION_MODULES, EDUCATION_TAGS, EDU_TAG_MR } from "../lib/educationModules";
import { PatientLanguageSelect } from "../components/PatientLanguageSelect";
import { usePatientEducationLanguage } from "../hooks/usePatientEducationLanguage";
import { patientLanguageName, languageDirection } from "../lib/patientLanguages";
import { shareEducationArticle } from "../lib/educationSharing";

// Education Hub — patient-agnostic library + AI article + quiz.
// Optional ?pid=PT-xxx logs a "share" audit trail when the doctor opens content.
export default function EducationHub() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const pid = sp.get("pid"); // optional patient scope for audit trail
  const { lang, setLang, patient, ready, error: patientError } = usePatientEducationLanguage(pid);
  const [articleDoc, setArticleDoc] = useState(null);
  const [articleError, setArticleError] = useState("");
  const [sharing, setSharing] = useState(false);
  const requestId = useRef(0);
  const [tag, setTag] = useState("All");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null); // module
  const [article, setArticle] = useState("");
  const [articleId, setArticleId] = useState("");
  const [loading, setLoading] = useState(false);
  // Quiz state
  const [quiz, setQuiz] = useState([]); // question objects
  const [quizLoading, setQuizLoading] = useState(false);
  const [qIdx, setQIdx] = useState(0);
  const [answered, setAnswered] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  useEffect(() => {
    requestId.current += 1;
    setSelected(null); setArticle(""); setArticleId(""); setArticleDoc(null);
    setQuiz([]); setLoading(false); setQuizLoading(false); setArticleError("");
  }, [pid, lang]);

  const L = (en, mr) => (lang === "mr" ? mr : en);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return EDUCATION_MODULES.filter((m) => {
      const inTag = tag === "All" || (m.tags || []).includes(tag);
      const inSearch = !needle || m.title_en.toLowerCase().includes(needle) || m.title_mr.includes(needle);
      return inTag && inSearch;
    });
  }, [q, tag]);
  const grouped = useMemo(() => filtered.reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m); return acc;
  }, {}), [filtered]);

  const openModule = async (m) => {
    if (!ready) return;
    const req = ++requestId.current;
    setArticleDoc(null); setArticleError("");
    setSelected(m); setArticle(""); setArticleId(""); setLoading(true);
    setQuiz([]); setQIdx(0); setScore(0); setFinished(false); setAnswered(null);
    try {
      const r = await api.post("/education/article", { topic: m.title_en, module_id: m.id, patient_id: pid || null, language: lang });
      if (req !== requestId.current) return;
      setArticleDoc({ ...r.data, patient_id: pid, module_id: m.id });
      setArticle(r.data.article || "");
      setArticleId(r.data.article_id || "");
    } catch (e) {
      if (req !== requestId.current) return;
      console.warn("article failed:", e);
      const message = e.response?.data?.detail || "Could not load article. Please retry.";
      setArticleError(message); toast.error(message);
    } finally { if (req === requestId.current) setLoading(false); }
  };

  const startQuiz = async () => {
    if (!selected) return;
    const req = requestId.current;
    setQuizLoading(true);
    try {
      const r = await api.post("/education/quiz", { topic: selected.title_en, language: lang, patient_id: pid || null });
      if (req !== requestId.current) return;
      setQuiz(r.data.questions || []); setQIdx(0); setScore(0); setFinished(false); setAnswered(null);
    } catch (e) {
      console.warn("quiz failed:", e);
      toast.error("Could not load quiz");
    } finally { setQuizLoading(false); }
  };

  const answer = (idx) => {
    if (answered !== null) return;
    setAnswered(idx);
    const correct = idx === quiz[qIdx].correctIndex;
    if (correct) setScore((s) => s + 1);
    setTimeout(() => {
      if (qIdx < quiz.length - 1) { setQIdx((i) => i + 1); setAnswered(null); }
      else setFinished(true);
    }, 1200);
  };

  const shareOnWhatsApp = async () => {
    if (!articleDoc || !ready) return;
    setSharing(true);
    try { await shareEducationArticle(articleDoc, patient); }
    catch (e) { toast.error(e.response?.data?.detail || e.message || "Could not share article"); }
    finally { setSharing(false); }
  };

  if (selected) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Toaster position="top-right" richColors />
        <div className="max-w-4xl mx-auto p-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Button variant="ghost" size="sm" onClick={() => { requestId.current += 1; setSelected(null); }} data-testid="edu-back-btn"><ArrowLeft size={14} className="mr-1" /> {L("Back to Academy", "अकादमीकडे परत जा")}</Button>
            <div className="ml-auto flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={shareOnWhatsApp} disabled={!articleDoc || loading || sharing || !ready} className="border-emerald-300 text-emerald-700" data-testid="edu-wa-btn"><MessageCircle size={14} className="mr-1" /> WhatsApp</Button>
              <Button size="sm" variant="outline" onClick={() => articleId && nav(`/education/print/${articleId}`)} disabled={!articleId || loading} className="border-slate-300 text-slate-700" data-testid="edu-pdf-btn"><Printer size={14} className="mr-1" /> {L("Print / Save PDF","प्रिंट / PDF")}</Button>
              <Button size="sm" onClick={startQuiz} disabled={quizLoading || !article} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="edu-quiz-btn"><Sparkles size={14} className="mr-1" /> {quizLoading ? L("Loading…","लोड होत आहे…") : L("Start Quiz","चाचणी सुरू")}</Button>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div data-testid="edu-article-language" className="text-xs text-pink-700 mb-2">{patientLanguageName(articleDoc?.language || lang)}{patient ? ` · ${patient.name}` : ""}</div>
            {articleError && <div role="alert" data-testid="edu-article-error" className="text-sm text-red-700 mb-3">{articleError}<Button variant="outline" className="ml-2" data-testid="edu-retry-article" onClick={() => openModule(selected)}>Retry</Button></div>}
            <h1 className="text-2xl font-bold text-slate-900 mb-1">{L(selected.title_en, selected.title_mr)}</h1>
            <div className="text-xs text-slate-500 mb-4">{selected.category} · {L(selected.duration_en, selected.duration_mr)}</div>

            {quiz.length === 0 && (
              loading
                ? <div className="text-sm text-slate-500 italic" data-testid="edu-loading">{L("Generating clinical narrative…","क्लिनिकल माहिती तयार होत आहे…")}</div>
                : <pre lang={articleDoc?.language || lang} dir={languageDirection(articleDoc?.language || lang)} className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-slate-800 font-sans" data-testid="edu-article-body">{article}</pre>
            )}

            {quiz.length > 0 && !finished && (
              <div data-testid="edu-quiz-panel">
                <div className="text-[10px] uppercase tracking-wider text-pink-600 font-semibold mb-1">{L("Knowledge Check","ज्ञान चाचणी")} — {qIdx + 1} / {quiz.length}</div>
                <div className="text-lg font-semibold text-slate-900 mb-3">{quiz[qIdx].question}</div>
                <div className="grid gap-2">
                  {(quiz[qIdx].options || []).map((opt, i) => {
                    const isCorrect = i === quiz[qIdx].correctIndex;
                    const cls = answered === null ? "border-slate-200 hover:border-pink-400 bg-white"
                      : i === answered ? (isCorrect ? "border-emerald-500 bg-emerald-50" : "border-rose-500 bg-rose-50")
                      : isCorrect ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white opacity-60";
                    return (
                      <button key={i} onClick={() => answer(i)} disabled={answered !== null}
                        className={`text-left border-2 rounded-md p-3 text-sm ${cls}`}
                        data-testid={`edu-quiz-opt-${i}`}>
                        {opt}
                        {answered !== null && i === quiz[qIdx].correctIndex && <Check size={14} className="text-emerald-600 inline-block ml-2" />}
                        {answered === i && i !== quiz[qIdx].correctIndex && <XIcon size={14} className="text-rose-600 inline-block ml-2" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {finished && (
              <div className="text-center py-8" data-testid="edu-quiz-finish">
                <div className="text-4xl mb-2">🏆</div>
                <div className="text-2xl font-bold uppercase tracking-tight">{L("Evaluation Complete","चाचणी पूर्ण झाली")}</div>
                <div className="text-lg text-slate-600 mt-2">{L("Your Score","तुमचा निकाल")}: <span className="text-pink-600 font-bold">{score} / {quiz.length}</span></div>
                <Button onClick={() => { setQuiz([]); setFinished(false); }} className="mt-6 bg-slate-900 text-white" data-testid="edu-quiz-restart">{L("Back to Article","लेखाकडे परत")}</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="edu-back-btn"><ArrowLeft size={14} className="mr-1" /> Back</Button>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><BookOpen size={20} className="text-pink-600" /> {L("Education Hub","शैक्षणिक केंद्र")}</h1>
          <div className="ml-auto w-full sm:w-64">
            <PatientLanguageSelect value={lang} onChange={setLang} disabled={!ready || !!patient?.preferred_language} testId="edu-language" label={patient ? `${patient.name} · patient language` : "Article language"} />
          </div>
        </div>
        {patientError && <p role="alert" data-testid="edu-patient-error" className="text-sm text-red-700 mb-4">{patientError}</p>}
        {!ready && !patientError && <p role="status" data-testid="edu-patient-loading" className="text-sm text-slate-600 mb-4">Loading patient language…</p>}

        <div className="mb-4 relative">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Search protocols…","प्रोटोकॉल शोधा…")} className="pl-10" data-testid="edu-search" />
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        </div>
        <div className="flex gap-1.5 flex-wrap mb-6">
          {EDUCATION_TAGS.map((t) => (
            <button key={t} onClick={() => setTag(t)} data-testid={`edu-tag-${t}`}
              className={`px-3 py-1 text-[11px] rounded-full border ${tag === t ? "bg-pink-600 text-white border-pink-600" : "bg-white text-slate-600 border-slate-200"}`}>
              {L(t, EDU_TAG_MR[t] || t)}
            </button>
          ))}
        </div>

        {Object.keys(grouped).length === 0 && (
          <div className="text-center text-slate-500 py-16">{L("No modules match your filter.","आपल्या फिल्टरशी जुळणारे मॉड्यूल नाहीत.")}</div>
        )}
        {Object.entries(grouped).map(([cat, mods]) => (
          <div key={cat} className="mb-8">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">{cat}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {mods.map((m) => (
                <button key={m.id} onClick={() => openModule(m)} disabled={!ready}
                  data-testid={`edu-module-${m.id}`}
                  className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:border-pink-400 hover:shadow-md text-left transition-all">
                  <div className="flex items-center gap-2 mb-1"><BookOpen size={12} className="text-pink-500" /><span className="font-semibold text-slate-900 text-sm">{L(m.title_en, m.title_mr)}</span></div>
                  <div className="text-[11px] text-slate-500 mb-2">{L(m.desc_en, m.desc_mr)}</div>
                  <div className="text-[10px] text-slate-400 flex gap-2">{L(m.duration_en, m.duration_mr)} · {(m.tags || [])[0]}</div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
